/**
 * Shared overlay primitives (06 §4.20): Dialog (centered modal), SlideOver
 * (desktop right panel), BottomSheet (mobile slide-up). All trap focus, close on
 * Esc/backdrop, and return focus to the trigger.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import styles from './Overlays.module.css';

function useEscape(onClose: () => void): void {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}

function useFocusTrap(active: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active) return;
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    node?.querySelector<HTMLElement>('[data-autofocus],button,input,textarea,a')?.focus();
    return () => prev?.focus();
  }, [active]);
  return ref;
}

interface BaseProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function Dialog({ open, onClose, children, title }: BaseProps & { title?: string }) {
  useEscape(onClose);
  const ref = useFocusTrap(open);
  if (!open) return null;
  return (
    <div className={styles.scrim} onMouseDown={onClose} role="presentation">
      <div
        ref={ref}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function SlideOver({
  open,
  onClose,
  children,
  title,
}: BaseProps & { title?: string }) {
  useEscape(onClose);
  const ref = useFocusTrap(open);
  if (!open) return null;
  return (
    <div className={styles.slideScrim} onMouseDown={onClose} role="presentation">
      <aside
        ref={ref}
        className={styles.slideover}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className={styles.panelHeader}>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
          <span className={styles.panelTitle}>{title}</span>
        </header>
        <div className={styles.panelBody}>{children}</div>
      </aside>
    </div>
  );
}

export function BottomSheet({ open, onClose, children }: BaseProps) {
  useEscape(onClose);
  const ref = useFocusTrap(open);
  if (!open) return null;
  return (
    <div className={styles.sheetScrim} onMouseDown={onClose} role="presentation">
      <div
        ref={ref}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className={styles.grabber} aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}
